/**
 * Typed wrappers over the Tauri commands in `CONTRACT.md`.
 *
 * Every call routes through `call()`, which falls back to `mocks.ts` when the
 * backend command is not implemented yet. That lets the UI workstreams build
 * against the real signatures before the Rust modules land — set
 * `VITE_IPC_MOCK=off` to disable the fallback and see raw backend errors.
 */

import type { UnlistenFn } from "@tauri-apps/api/event";
import * as mocks from "./mocks";
import * as wasmEngine from "../wasm-engine/engine";
import type {
  CaseDraft,
  CheckoutCandidate,
  EngineSelection,
  EngineSetupRequest,
  EngineSetupStatus,
  EngineSetupStep,
  EngineStatus,
  NativeEngineProbe,
  PythonCandidate,
  CaseManifest,
  CommandPreview,
  DatasetScan,
  DockerStatus,
  GpuStatus,
  JobHandle,
  JobLogEvent,
  JobProgressEvent,
  JobSpec,
  JobStageEvent,
  JobStateEvent,
  JobStatus,
  LesionLoadTable,
  NiftiHeaderInfo,
  OutputNode,
  PipelineKind,
  Preferences,
  QfvTable,
  RecentCase,
  ResultsSummary,
  StageSelection,
  SubjectScan,
  SystemStatus,
  VolumeToolRequest,
  VolumeToolResult,
  CohortScan,
  IntegrityProgress,
  PreparedInput,
  Project,
  ProjectRun,
  ProjectSummary,
  RoleChoice,
} from "./contract";
import {
  EVT_ENGINE_SETUP_LOG,
  EVT_ENGINE_SETUP_STATE,
  EVT_ENGINE_SETUP_STEP,
  EVT_JOB_LOG,
  EVT_INTEGRITY,
  EVT_JOB_PROGRESS,
  EVT_JOB_STAGE,
  EVT_JOB_STATE,
} from "./contract";

/**
 * Browser build: there is no Tauri backend, ever. Real commands are served by
 * `wasm-engine/engine.ts`; anything it does not implement falls back to
 * `mocks.ts` (same fixtures the original desktop UI was developed against)
 * rather than throwing, so unported screens still render plausible data
 * instead of a wall of errors.
 */
const WASM_COMMANDS: Record<string, (args: Record<string, unknown>) => unknown> = {
  get_system_status: () => wasmEngine.systemStatusWeb(),
  get_engine_status: () => wasmEngine.engineStatusWeb(),
  pick_directory: () => wasmEngine.pickDirectoryWeb(),
  load_example_subject: (a) => wasmEngine.loadExampleSubjectWeb((a.kind as "dwi" | "pwi" | undefined) ?? "dwi"),
  scan_subject: (a) => wasmEngine.scanSubjectWeb(a.subjectDir as string),
  create_case: (a) => wasmEngine.createCaseWeb(a.draft as Parameters<typeof wasmEngine.createCaseWeb>[0]),
  start_job: (a) => wasmEngine.startJobWeb(a.spec as Parameters<typeof wasmEngine.startJobWeb>[0]),
  get_job: (a) => wasmEngine.getJobWeb(a.jobId as string),
  // Without this the mock job list answered, the live run was missing from
  // it, and every window focus marked it "failed" while it kept running.
  list_jobs: (a) => wasmEngine.listJobsWeb((a.limit as number | undefined) ?? 50),
  list_outputs: (a) => wasmEngine.listOutputsWeb(a.caseId as string),
  // E2E test patch: the Output browser's only data source fell back to an
  // always-empty mock.
  list_output_dir: (a) => ({ ...wasmEngine.listOutputsWeb(""), root: a.root as string }),
  get_asset_url: (a) => wasmEngine.getAssetUrlWeb(a.path as string),
  pick_batch_directory: () => wasmEngine.pickBatchDirectoryWeb(),
  run_batch: (a) =>
    wasmEngine.runBatchWeb(
      a.subjectDirs as string[],
      a.opts as Parameters<typeof wasmEngine.runBatchWeb>[1],
    ),
  // Real IndexedDB-backed case-metadata persistence (`lib/project-store.ts`)
  // — saves/reloads which subject and pipeline settings a case had, not its
  // computed outputs (those stay in-memory only, lost on reload; see that
  // module's header for why this scope split is correct, not a shortcut).
  // `save_project_record`/`list_projects`/etc. are a separate, broader
  // cohort/`Project` concept — not this command family, left untouched here.
  list_recent_cases: () => wasmEngine.listRecentCasesWeb(),
  open_case: (a) => wasmEngine.openCaseWeb(a.caseJsonPath as string),
  // E2E test patch: without this the mock manifest (a developer's local
  // inputRoot) replaced the real case, so every stage ran on no files.
  refresh_case_nodes: (a) => {
    const manifest = wasmEngine.getCaseWeb(a.caseId as string);
    if (!manifest) throw new Error(`No open case "${a.caseId as string}".`);
    // E2E test patch: expose the real NIfTI outputs as case nodes so the
    // viewer and the Tools have something to load.
    const nodes = wasmEngine
      .listOutputsWeb(manifest.caseId)
      .tree.filter((n) => n.kind === "nifti")
      .map((n) => {
        const isMask = /mask|stroke|HP-|hypoperfusion/i.test(n.name);
        return {
          id: n.relPath,
          label: n.name.replace(/\.nii(\.gz)?$/, ""),
          relPath: n.relPath,
          origin: "output" as const,
          space: /MNI152/.test(n.name) ? ("MNI152" as const) : ("native" as const),
          kind: isMask ? ("mask" as const) : ("image" as const),
          derived: true,
          meta: {},
        };
      });
    return {
      ...manifest,
      images: nodes.filter((n) => n.kind === "image"),
      masks: nodes.filter((n) => n.kind === "mask"),
    };
  },
  save_case: (a) => wasmEngine.saveProjectWeb((a.manifest as CaseManifest).caseId),
  delete_case: (a) => wasmEngine.deleteProjectWeb(a.caseId as string),
};

/** Split a backend error into the user-facing sentence and technical detail. */
export function splitError(error: unknown): { message: string; detail: string | null } {
  const text = String(error);
  const idx = text.indexOf("\n---\n");
  return idx === -1
    ? { message: text, detail: null }
    : { message: text.slice(0, idx), detail: text.slice(idx + 5) };
}

async function call<T>(command: string, args: Record<string, unknown>, mock?: () => T): Promise<T> {
  const real = WASM_COMMANDS[command];
  if (real) return (await real(args)) as T;
  if (mock) return mock();
  throw new Error(
    `${command} is not available in the browser build yet.\n---\nThis screen needs a stage this WASM port has not wired in. See openads-wasm/desk's port notes.`,
  );
}

// ---------------------------------------------------------------- system ---

export const getSystemStatus = (refresh = false) =>
  call<SystemStatus>("get_system_status", { refresh }, mocks.systemStatus);

export const getDockerStatus = () => call<DockerStatus>("get_docker_status", {}, mocks.dockerStatus);

export const getGpuStatus = () => call<GpuStatus>("get_gpu_status", {}, mocks.gpuStatus);

// ---------------------------------------------------------------- engine ---

export const getEngineStatus = (refresh = false) =>
  call<EngineStatus>("get_engine_status", { refresh }, mocks.engineStatus);

export const setEngineSelection = (kind: EngineSelection) =>
  call<EngineStatus>("set_engine_selection", { kind }, mocks.engineStatus);

export const detectEngineCandidates = () =>
  call<{ pythons: PythonCandidate[]; checkouts: CheckoutCandidate[] }>(
    "detect_engine_candidates",
    {},
    mocks.engineCandidates,
  );

export const validateEngine = (pythonPath?: string | null, checkoutPath?: string | null) =>
  call<NativeEngineProbe>("validate_engine", { pythonPath, checkoutPath }, mocks.nativeProbe);

export const startEngineSetup = (request: EngineSetupRequest) =>
  call<EngineSetupStatus>("start_engine_setup", { request }, () => ({
    setupId: "mock",
    state: "completed" as const,
    steps: [],
    logPath: "",
    errorMessage: null,
  }));

export const getEngineSetupStatus = () =>
  call<EngineSetupStatus | null>("get_engine_setup_status", {}, () => null);

export const cancelEngineSetup = () => call<void>("cancel_engine_setup", {}, () => undefined);

export const enginePaths = () =>
  call<{ managedRoot: string; venv: string; appData: string }>("engine_paths", {}, () => ({
    managedRoot: "",
    venv: "",
    appData: "",
  }));

// ------------------------------------------------------------ input scan ---

export const scanSubject = (subjectDir: string) =>
  call<SubjectScan>("scan_subject", { subjectDir }, () => mocks.subjectScan(subjectDir));

export const scanDatasetRoot = (root: string) =>
  call<DatasetScan>("scan_dataset_root", { root }, () => ({ subjects: [] }));

/**
 * Read every subject under a dataset root and work out what they have in
 * common. There is no mock: a dataset review that invented subjects would be
 * worse than an empty screen, since the whole point is to be believed.
 */
export const prepareSubjectInput = (subjectId: string, choices: RoleChoice[]) =>
  call<PreparedInput>("prepare_subject_input", { subjectId, choices }, () => {
    throw new Error("Assigning files needs the desktop app.");
  });

// ------------------------------------------------------------ projects ---

export const listProjects = () => call<ProjectSummary[]>("list_projects", {}, () => []);

export const loadProject = (id: string) =>
  call<Project>("load_project", { id }, () => {
    throw new Error("Projects need the desktop app.");
  });

export const saveProjectRecord = (project: Project) =>
  call<Project>("save_project_record", { project }, () => project);

export const deleteProject = (id: string) =>
  call<void>("delete_project", { id }, () => undefined);

export const recordProjectRun = (id: string, run: ProjectRun) =>
  call<Project>("record_project_run", { id, run }, () => {
    throw new Error("Projects need the desktop app.");
  });

export const addProjectNote = (id: string, text: string) =>
  call<Project>("add_project_note", { id, text }, () => {
    throw new Error("Projects need the desktop app.");
  });

/** Open every image under a dataset root. Streams `scan://integrity`. */
export const checkCohortIntegrity = (root: string) =>
  call<void>("check_cohort_integrity", { root }, () => {
    throw new Error("The integrity check needs the desktop app.");
  });

export const cancelCohortIntegrity = () =>
  call<void>("cancel_cohort_integrity", {}, () => undefined);

export const onIntegrityProgress = (fn: (e: IntegrityProgress) => void) =>
  subscribe<IntegrityProgress>(EVT_INTEGRITY, fn);

export const scanCohort = (root: string) =>
  call<CohortScan>("scan_cohort", { root }, () => {
    throw new Error("Dataset review needs the desktop app; there is nothing to read in a browser.");
  });

export const readNiftiHeader = (path: string) =>
  call<NiftiHeaderInfo>("read_nifti_header", { path }, () => mocks.niftiHeader(path));

// ------------------------------------------------------------------ case ---

export const createCase = (draft: CaseDraft) =>
  call<CaseManifest>("create_case", { draft }, () => mocks.caseManifest(draft));

export const openCase = (caseJsonPath: string) =>
  call<CaseManifest>("open_case", { caseJsonPath }, () => mocks.caseManifest());

export const saveCase = (manifest: CaseManifest) =>
  call<void>("save_case", { manifest }, () => undefined);

export const listRecentCases = () =>
  call<RecentCase[]>("list_recent_cases", {}, mocks.recentCases);

export const deleteCase = (caseId: string, deleteFiles: boolean) =>
  call<void>("delete_case", { caseId, deleteFiles }, () => undefined);

export const refreshCaseNodes = (caseId: string) =>
  call<CaseManifest>("refresh_case_nodes", { caseId }, () => mocks.caseManifest());

// ------------------------------------------------------------------- job ---

export const startJob = (spec: JobSpec) =>
  call<JobHandle>("start_job", { spec }, () => mocks.startJob(spec));

export const cancelJob = (jobId: string) =>
  call<void>("cancel_job", { jobId }, () => mocks.cancelJob(jobId));

export const getJob = (jobId: string) =>
  call<JobStatus>("get_job", { jobId }, () => mocks.jobStatus(jobId));

export const listJobs = (caseId?: string, limit?: number) =>
  call<JobStatus[]>("list_jobs", { caseId, limit }, mocks.jobList);

export const previewCommand = (spec: JobSpec) =>
  call<CommandPreview>("preview_command", { spec }, () => mocks.previewCommand(spec));

export const readJobLog = (jobId: string, tailLines?: number) =>
  call<{ lines: string[]; truncated: boolean }>("read_job_log", { jobId, tailLines }, () => ({
    lines: [],
    truncated: false,
  }));

// --------------------------------------------------------------- outputs ---

export const listOutputs = (caseId: string) =>
  call<{ root: string; tree: OutputNode[] }>("list_outputs", { caseId }, mocks.outputs);

/** The output tree under a folder, for browsing results that outlive a case. */
export const listOutputDir = (root: string) =>
  call<{ root: string; tree: OutputNode[] }>("list_output_dir", { root }, () => ({
    root,
    tree: [],
  }));

export const getAssetUrl = (path: string) =>
  call<{ url: string }>("get_asset_url", { path }, () => ({ url: "" }));

export const readTextFile = (path: string, maxBytes?: number) =>
  call<{ content: string; truncated: boolean }>("read_text_file", { path, maxBytes }, () => ({
    content: "",
    truncated: false,
  }));

export const openPath = (path: string) => call<void>("open_path", { path }, () => undefined);

export const revealPath = (path: string) => call<void>("reveal_path", { path }, () => undefined);

// --------------------------------------------------------------- results ---

export const getResultsSummary = (caseId: string) =>
  call<ResultsSummary>("get_results_summary", { caseId }, () => mocks.resultsSummary(caseId));

export const readQfvTable = (path: string) =>
  call<QfvTable>("read_qfv_table", { path }, () => mocks.qfvTable());

export const readLesionLoadTable = (path: string) =>
  call<LesionLoadTable>("read_lesionload_table", { path }, () => mocks.lesionLoadTable());

export const exportResults = (
  caseId: string,
  destDir: string,
  formats: ("pdf" | "csv" | "nifti" | "json")[],
) =>
  call<{ written: string[]; warnings: string[] }>("export_results", { caseId, destDir, formats }, () => ({
    written: [],
    warnings: [],
  }));

// ----------------------------------------------------------- preferences ---

export const getPreferences = () => call<Preferences>("get_preferences", {}, mocks.preferences);

export const setPreferences = (prefs: Partial<Preferences>) =>
  call<Preferences>("set_preferences", { prefs }, () => ({ ...mocks.preferences(), ...prefs }));

export const pickDirectory = (title: string, defaultPath?: string) =>
  call<{ path: string | null }>("pick_directory", { title, defaultPath }, () => ({ path: null }));

/** Real multi-subject batch pick + run, ported from the reference
 *  `batch_run_combined.sh`: group a picked folder tree by subject, run the
 *  real combined DWI+PWI job once per subject, one failed subject does not
 *  abort the rest. See `wasm-engine/engine.ts`'s `runBatchWeb` for the real
 *  orchestration this wraps. */
export const pickBatchDirectory = () =>
  call<{ subjectDirs: string[] }>("pick_batch_directory", {}, () => {
    throw new Error("Batch processing needs the WASM engine (wasmEngine.pickBatchDirectoryWeb).");
  });

export const runBatch = (
  subjectDirs: string[],
  opts: { pipelineKind: PipelineKind; stages: StageSelection; gpuIndex: string; dockerImage: string | null; cleanupEnabled: boolean },
) =>
  call<wasmEngine.BatchResult>("run_batch", { subjectDirs, opts }, () => {
    throw new Error("Batch processing needs the WASM engine (wasmEngine.runBatchWeb).");
  });

/** The real validation subject, fetched from `public/demo/raw-dwi/` instead
 *  of picked — the only "open a subject" path that works without the native
 *  OS directory picker, so both a human and browser automation can use it. */
/** `kind` picks the DWI-only or the DWI+PWI example subject. */
export const loadExampleSubject = (kind: "dwi" | "pwi" = "dwi") =>
  call<{ path: string }>("load_example_subject", { kind }, () => {
    throw new Error("The example subject needs the WASM engine (wasmEngine.loadExampleSubjectWeb).");
  });

export const pickFiles = (title: string, extensions: string[], multiple: boolean) =>
  call<{ paths: string[] }>("pick_files", { title, extensions, multiple }, () => ({ paths: [] }));

export const runVolumeTool = (request: VolumeToolRequest) =>
  call<VolumeToolResult>("run_volume_tool", { request }, () => ({
    outputPath: null,
    kind: "stats",
    stats: {},
    message: "Volume tools need the Tauri backend and the OpenADS Docker image.",
  }));

// ---------------------------------------------------------------- events ---

/**
 * Browser build: there is no Tauri event bridge. The WASM engine's job runner
 * and `mocks.startJob` both publish to the same `mocks.mockBus` (see
 * `wasm-engine/engine.ts`), so one subscription covers real and mocked jobs.
 */
async function subscribe<T>(topic: string, fn: (payload: T) => void): Promise<UnlistenFn> {
  return mocks.mockBus.on<T>(topic, fn);
}

export const onJobLog = (fn: (e: JobLogEvent) => void) => subscribe<JobLogEvent>(EVT_JOB_LOG, fn);

export const onJobStage = (fn: (e: JobStageEvent) => void) =>
  subscribe<JobStageEvent>(EVT_JOB_STAGE, fn);

export const onJobProgress = (fn: (e: JobProgressEvent) => void) =>
  subscribe<JobProgressEvent>(EVT_JOB_PROGRESS, fn);

export const onJobState = (fn: (e: JobStateEvent) => void) =>
  subscribe<JobStateEvent>(EVT_JOB_STATE, fn);

export const onEngineSetupLog = (fn: (e: JobLogEvent) => void) =>
  subscribe<JobLogEvent>(EVT_ENGINE_SETUP_LOG, fn);

export const onEngineSetupStep = (fn: (e: EngineSetupStep) => void) =>
  subscribe<EngineSetupStep>(EVT_ENGINE_SETUP_STEP, fn);

export const onEngineSetupState = (fn: (e: EngineSetupStatus) => void) =>
  subscribe<EngineSetupStatus>(EVT_ENGINE_SETUP_STATE, fn);
